import { memo, useState, useEffect, useCallback } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Box,
  Tooltip,
} from '@mui/material'
import { posStorePromotionService } from '../../services'
import TableLoadingRow from '../shared/TableLoadingRow'
import TableEmptyState from '../shared/TableEmptyState'
import {
  formatActualMrp,
  formatPromotionDate,
  formatPromoPrice,
  isPromoPriceAboveMrp,
  PROMO_PRICE_ABOVE_MRP_LABEL,
} from '../../utils/posPromotionExport'

const STATUS_COLORS = {
  active: 'success',
  inactive: 'default',
  expired: 'error',
  scheduled: 'warning',
}

const TYPE_COLORS = {
  tp: 'primary',
  discount: 'warning',
  percentage: 'secondary',
  bonus: 'success',
}

const COLUMN_COUNT = 12

function PromoCatalogTable({ filters, resetKey }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  useEffect(() => {
    setPage(0)
  }, [resetKey])

  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true)
      const response = await posStorePromotionService.list({
        page: page + 1,
        limit: rowsPerPage,
        search: filters.search || undefined,
        status: filters.status || 'all',
        type: filters.type || undefined,
        validFrom: filters.validFrom || undefined,
        validTo: filters.validTo || undefined,
      })
      setItems(Array.isArray(response.items) ? response.items : [])
      setTotalCount(response.pagination?.total || 0)
    } catch (error) {
      console.error('Error fetching POS store promotions:', error)
      setItems([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, filters])

  useEffect(() => {
    fetchPromotions()
  }, [fetchPromotions])

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f7fa' }}>
              <TableCell sx={{ fontWeight: 600 }}>Promotion ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Article Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actual MRP</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Valid From</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Valid To</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Min Invoice</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableLoadingRow colSpan={COLUMN_COUNT} />
            ) : items.length === 0 ? (
              <TableEmptyState colSpan={COLUMN_COUNT} message="No promotions found" />
            ) : (
              items.map((promo) => {
                const actualMrp = formatActualMrp(promo.actual_mrp)
                const promoPriceAboveMrp = isPromoPriceAboveMrp(promo)
                return (
                <TableRow
                  key={promo.id}
                  hover
                  sx={promoPriceAboveMrp ? { bgcolor: '#FFF7ED' } : undefined}
                >
                  <TableCell>{promo.promotion_id}</TableCell>
                  <TableCell>{promo.sku}</TableCell>
                  <TableCell>{promo.product_name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={promo.type}
                      color={TYPE_COLORS[promo.type] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {actualMrp != null ? `৳${actualMrp.toFixed(2)}` : '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <span>{formatPromoPrice(promo)}</span>
                      {promoPriceAboveMrp ? (
                        <Tooltip title="Promo price is above MRP. Applied reimbursement is 0 because the promo price is not below MRP.">
                          <Chip
                            size="small"
                            color="warning"
                            label={PROMO_PRICE_ABOVE_MRP_LABEL}
                          />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>{promo.discount_unit}</TableCell>
                  <TableCell>{promo.priority}</TableCell>
                  <TableCell>{formatPromotionDate(promo.valid_from) || 'N/A'}</TableCell>
                  <TableCell>{formatPromotionDate(promo.valid_to) || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={promo.status || '—'}
                      color={STATUS_COLORS[promo.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {Number(promo.min_invoice_value) > 0
                      ? `৳${Number(promo.min_invoice_value).toFixed(2)}`
                      : '—'}
                  </TableCell>
                </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </>
  )
}

export default memo(PromoCatalogTable)
